import api from '../../../services/api';

// P7a — E15 AI bulguları ve aksiyon merkezi. Uçlar (BE ile birebir):
//   GET   v1/performance-ai-center/summary?periodId[eq]=&detectedAt[gte]=&detectedAt[lte]=   → M56, M57, M43, M44 (+ durum sayıları)
//   GET   v1/performance-ai-center/findings?periodId[eq]=&status[in]=&ruleCode[eq]=&severity[eq]=&scopeType[eq]=&ownerEmployeeId[eq]=
//         &detectedAt[gte]=&detectedAt[lte]=&pageNumber=&pageSize=                               → bulgu kuyruğu (PmInsightDto)
//   GET   v1/performance-ai-center/quality?from=&to=&kind=                                        → M71–M74 (yalnız İK)
//   GET   v1/performance-ai-center/quality/statements?status=pending|reviewed&kind=&pageNumber=&pageSize=  (yalnız İK)
//   PATCH v1/performance-ai-center/quality/statements/{id} {verdict}
// Bulgu geçişleri ve aksiyonlar mevcut uçlardadır (v1/performance-insights/{id}/status, v1/performance-actions).
// api.js GET'te reject ETMEZ — hata sinyali res.data.status (false → "yüklenemedi", boş liste → "veri yok").

export const FINDINGS_ENDPOINT = '/v1/performance-ai-center/findings';
export const OPEN_STATUSES = 'new,reviewing';
export const LIVE_STATUSES = 'new,reviewing,actioned';
export const KPI_CODES = ['M56', 'M57', 'M43', 'M44'];
export const QUALITY_CODES = ['M71', 'M72', 'M73', 'M74'];
export const DRAFT_KINDS = ['manager_feedback', 'self_review', 'profile_summary'];
export const VERDICTS = ['correct', 'incorrect', 'unverifiable'];

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

export class LoadError extends Error {}

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new LoadError(res?.data?.message || 'error');
  return data;
}

const pageRows = (data) => (Array.isArray(data) ? data : (data?.data || []));

export const fetchSummary = (periodId) => get('/v1/performance-ai-center/summary', periodId ? { 'periodId[eq]': periodId } : {});
export const fetchQuality = ({ from, to, kind } = {}) => get('/v1/performance-ai-center/quality', {
  ...(from ? { from } : {}), ...(to ? { to } : {}), ...(kind ? { kind } : {}),
});
export const fetchPeriods = async () => {
  const res = await api.get('/v1/evaluation-periods', { params: { pageSize: 200, orderBy: 'startDate', orderDir: 'desc' } });
  return pageRows(payload(res) || []);
};

export const reviewStatement = async (id, verdict) => {
  try {
    await api.patch(`/v1/performance-ai-center/quality/statements/${id}`, { verdict });
    return true;
  } catch {
    return false;
  }
};

// Aksiyon durumu (tamamlanırken isteğe bağlı sonuç notu — analiz §26 "sonuç ve insan geri bildirimi kayıt altına alınır").
export const changeActionStatus = async (id, status, outcomeNote) => {
  try {
    await api.patch(`/v1/performance-actions/${id}/status`, { status, ...(outcomeNote ? { outcomeNote } : {}) });
    return true;
  } catch {
    return false;
  }
};

/** Tablo süzgecinin `detectedAt[between]` değeri uçta iki ayrı parametredir. */
export function toFindingParams(params, extra) {
  const out = { ...params, ...extra };
  const between = out['detectedAt[between]'];
  if (between) {
    const [from, to] = String(between).split(',');
    if (from) out['detectedAt[gte]'] = from;
    if (to) out['detectedAt[lte]'] = to;
    delete out['detectedAt[between]'];
  }
  Object.keys(out).forEach((k) => { if (out[k] == null || out[k] === '') delete out[k]; });
  return out;
}

export const metricOf = (list, code) => (list || []).find((m) => m.code === code) || null;
// Payda 0 ya da değer yok → "Veri Yok" (kart standardı, analiz §25).
export const hasValue = (m) => !!m && m.value != null && !(m.unit !== 'count' && m.denominator != null && Number(m.denominator) === 0);

// ------------------------------------------------------------------ bağlantı sözleşmesi
// "Bulgu → kaynak ekranın aynı filtreleri" (analiz §18): dönem korunur. Kişi kapsamlı/kalemli bulgular E14 profiline gider.
export const profileLink = (employeeId, periodId, from) => {
  const p = new URLSearchParams();
  if (periodId) p.set('periodId', periodId);
  if (from) p.set('from', from);
  const s = p.toString();
  return `/performance/profile/${employeeId}${s ? `?${s}` : ''}`;
};

const operations = (periodId) => `/performance/operations${periodId ? `?period=${periodId}` : ''}`;

/**
 * Bulgunun kaynak ekranı (İK yüzeyleri yalnız İK'ya). null = bağlantı yok (yönetici için İK ekranı gösterilmez).
 */
export function insightSourceLink(insight, isHr) {
  if (!insight) return null;
  const periodId = insight.periodId;
  if (insight.scopeType === 'employee' && insight.ruleCode === 'pm.review.attention') return profileLink(insight.scopeId, periodId);
  switch (insight.ruleCode) {
    case 'pm.overdue.concentration':
    case 'pm.stage.slowdown':
    case 'pm.due.pileup':
    case 'pm.goal.decline':
    case 'data.completeness':
      return isHr ? operations(periodId) : null;
    case 'pm.rating.deviation':
      return isHr ? '/performance/report-center' : null;
    case 'pm.potential.missing':
      return isHr ? '/talent/potential-assessments' : null;
    case 'pm.succession.gap':
      return isHr ? '/talent/succession' : null;
    default:
      return null;
  }
}

// Bulgu detayındaki "Hesaplama" satırları — kanıtın sayısal alanları (izinli kanıt anahtarları; metin yok).
export const EVIDENCE_METRIC_KEYS = [
  'M04', 'M46', 'M18', 'M20', 'M22',
  'overdueTasks', 'assigneePopulation', 'topAssigneeCount', 'topAssigneeShare',
  'highBand', 'missing', 'gapPositions',
  'openTasks', 'upcomingTasks', 'untouchedTasks', 'untouchedShare', 'nearestDueInDays',
  'cohortN', 'eligibleN', 'coverage', 'avgBefore', 'avgAfter', 'goalsBefore', 'goalsAfter',
  'matched', 'bigGap', 'managerHigher', 'selfHigher',
  'teamN', 'peerN', 'coveredN', 'persons', 'pairs', 'periods',
];
// 0–1 oranları yüzde olarak gösterilir.
export const FRACTION_KEYS = new Set(['topAssigneeShare', 'untouchedShare', 'coverage']);

export function evidenceRows(insight) {
  const ev = insight?.evidence || {};
  return EVIDENCE_METRIC_KEYS
    .filter((k) => ev[k] !== undefined && ev[k] !== null)
    .map((k) => ({ key: k, value: FRACTION_KEYS.has(k) ? Math.round(Number(ev[k]) * 10000) / 100 : ev[k], percent: FRACTION_KEYS.has(k) }));
}

/** İfade kaynağının kısa etiketi (kod + tür). */
export const sourceKey = (source) => `performance.aiCenter.sourceType.${source?.type || 'metric'}`;
